<script lang="ts" setup>
  import { useBreakpoints } from '@storefront/storefront';
  import type { Paginator } from '@othercommerce/domain';
  import type { ListOrderItemResource, OrderResource } from '@storefront/resources';
  import { type FormContextInterface } from '@othercommerce/foundation';

  interface Props {
    products: Paginator<ListOrderItemResource>;
    order?: OrderResource;
    refund: string;
    back?: string;
  }

  defineProps<Props>();

  const breakpoints = useBreakpoints();

  function onChoose(item: any, ctx: FormContextInterface) {
    const items = ctx.data.value.items;
    const index = items.indexOf(item.id);

    if (index !== -1) {
      items.splice(index, 1);
    } else {
      items.push(item.id);
    }
  }

  function isSelected(item: any, data: any) {
    return data.items.includes(item.id);
  }
</script>

<template>
  <SfTile large v-if="products.data.length > 0" class="account-products">
    <template #title v-if="breakpoints.lg">
      <h1 class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.REFUND_CHOOSE_PRODUCTS') }}
      </h1>
    </template>
    <template #default>
      <FormController
        method="POST"
        :action="refund"
        v-slot="{data, ctx}" :data="{items: [], order}"
        class="account-products__content"
      >
        <div class="account-products__buttons">
          <SfButton primary type="submit=" :disabled="data.items.length === 0">{{ $t('SHOP.ACCOUNT.HEADER.REFUND_CREATE') }}</SfButton>
          <SfButton primary :href="back" v-if="back">{{ $t('SHOP.ACTION.CANCEL') }}</SfButton>
        </div>
        <SfListCard class="line"
          large v-for="product in products.data"
          interactive @click="onChoose(product, ctx)"
          @keydown.space="onChoose(product, ctx)"
          :selected="isSelected(product, data)"
          tabindex="0"
          role="button"
        >
          <template #image>
            <MediaPicture :photo="product.photo" sizes="140x140 2x, 70x70" />
          </template>
          <template #default>
            <div class="line__content">
              <h2 class="line__heading" v-text="product.variantTitle" />
              <p v-if="product.variantSku" class="line__property" v-html="$t('SHOP.REFUND.LABEL.PROPERTY_SKU', { code: product.variantSku })" />
              <p class="line__property" v-html="$t('SHOP.REFUND.LABEL.PROPERTY_ORDER_NUMBER', { number: product.order.number })" />
              <p class="line__property" v-if="product.returnExpiresAt" v-html="$t('SHOP.REFUND.LABEL.RETURN_EXPIRES_AT', { date: product.returnExpiresAt })" />
            </div>
          </template>
          <template #icons>
            <div v-if="product.regularGross && product.regularGross !== product.priceGross" class="line__discount">
              {{ $t('SHOP.REFUND.LABEL.DISCOUNT') }}
              <SfFormattedPercentage :value="((Number(product.regularGross) - Number(product.priceGross)) / Number(product.regularGross)) * 100" :negative="false" />
            </div>
          </template>
          <template #actions>
            <div class="line__pricing">
              <div v-if="product.regularGross && product.regularGross !== product.priceGross" class="line__regular">
                <SfMoney :code="product.currencyCode" :value="product.regularGross" :decimals="product.decimals" />
              </div>
              <div class="line__price">
                <SfUnit :unit="product.stockUnit" :quantity="product.amount" />
                &times;
                <SfMoney :code="product.currencyCode" :value="product.priceGross" :decimals="product.decimals" />
              </div>
            </div>
          </template>
        </SfListCard>
      </FormController>
    </template>
  </SfTile>

  <SfTile v-else>
    <div class="account-products__buttons">
      <SfButton primary v-if="back" :href="back">{{ $t('SHOP.COMMON.ACTION.CANCEL') }}</SfButton>
    </div>
    <SfDataPlaceholder>
      <template v-if="order">
        {{ $t('SHOP.REFUND.MESSAGE.NO_PRODUCTS_TO_REFUND_IN_ORDER') }}
      </template>
      <template v-else>
        {{ $t('SHOP.REFUND.MESSAGE.NO_PRODUCTS_TO_REFUND') }}
      </template>
    </SfDataPlaceholder>
  </SfTile>
  <SfPagination :pagination="products" />
</template>

<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  :deep(.list-card--selected) {
    .line__property {
      color: var(--sf-list-card-color-selected);
    }
  }

  .line {
    &__heading {
      font-weight: var(--font-weight-bold);
      font-size: 1rem;
      margin-bottom: 0.375rem;
    }

    &__discount {
      font-weight: var(--font-weight-bold);
      font-size: 0.875rem;
    }

    &__property {
      font-size: 0.75rem;
      color: var(--secondary-color);

      :deep(span) {
        font-weight: var(--font-weight-semi);
        word-break: break-word;
      }
    }

    &__regular {
      color: var(--danger-color);
      font-size: 0.875rem;
      text-decoration: line-through;
    }

    &__price {
      white-space: nowrap;
    }

    :deep(.list-card__image) {
      flex: 0 0 70px;

      @include media.query(md) {
        flex: 0 0 45px;
      }

      picture, img {
        display: flex;
        aspect-ratio: 1/1;
      }
    }
  }

  .account-products {
    &__buttons {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
      row-gap: 1.25rem;
    }
  }

  @include media.query(lg) {
    .account-products {
      row-gap: 1rem;
    }
  }
</style>
